import axios, { AxiosInstance } from 'axios'

import { captureException } from '@/lib/observability'
import { parseQuotaExceededPayload, QuotaExceededError } from '@/lib/errorMessage'
import type {
  Alert,
  AlertSubscribeRequest,
  AcquisitionTarget,
  AuthToken,
  ChangelogEntry,
  Company,
  CompanyComparisonItem,
  CompanyList,
  DDReport,
  FeatureFrequency,
  FundingRound,
  HeadcountSnapshot,
  HiringData,
  HiringTrends,
  LeadershipChange,
  LoginRequest,
  MarketSummary,
  PaginatedCompanies,
  PositioningChange,
  PositioningSnapshot,
  PositioningSummary,
  PriceDistributionBucket,
  PricingHistory,
  PricingPlan,
  PricingTrend,
  ReviewAggregate,
  SentimentHistoryPoint,
  SentimentLeaderboardItem,
  SignupRequest,
  TechStackData,
  User,
  EffectiveWatchlist,
  WatchlistCompany,
  ServedCycle,
  CompanyComplianceCerts,
  CompanyCustomerLogos,
  CompanyIntegrationIntelligence,
  EmployeeReviewsResponse,
  EmployeeDepthResponse,
  WorkforceImmigrationResponse,
} from '@/types'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.pulsesignal.co'

// ─── Axios instance ───────────────────────────────────────────────────────────

const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  return config
})

// Handle 401/403 (auth failures) by clearing session and redirecting to login.
// When the backend rejects with reason=session_revoked or
// reason=upgrade_required, surface that to /login as a banner so the user
// understands why they were kicked.
api.interceptors.response.use(
  (response) => {
    // CA-4: soft quota-warning surfacing. When a quota-consuming action
    // succeeds but the user is close to their per-cycle ceiling, the backend
    // tags the 2xx response with X-Quota-Warning (+ X-Quota-Used/-Limit/-Reset).
    // Dispatch a window event that <QuotaWarningBanner /> in DashboardLayout
    // picks up, so users get a heads-up before they hit the hard 429 wall.
    if (typeof window !== 'undefined') {
      const headers = response.headers as Record<string, string | undefined> | undefined
      const warning = headers?.['x-quota-warning']
      if (warning) {
        const parseNum = (v: string | undefined): number | null => {
          if (v == null) return null
          const n = Number(v)
          return Number.isFinite(n) ? n : null
        }
        try {
          window.dispatchEvent(
            new CustomEvent('ps:quota-warning', {
              detail: {
                endpoint: (response.config?.url as string) || '',
                message: warning,
                used: parseNum(headers?.['x-quota-used']),
                limit: parseNum(headers?.['x-quota-limit']),
                reset: headers?.['x-quota-reset'] || null,
              },
            }),
          )
        } catch {
          // CustomEvent unsupported in this runtime; nothing to do.
        }
      }
    }
    return response
  },
  (error) => {
    const status = error.response?.status

    // 429 surfacing. Backend enforces per-tier daily quotas and per-route
    // rate limits (e.g. /login is 5/min). Without a global surface,
    // callers see a raw error and don't know it's a quota issue or that
    // a higher tier raises the limit. Dispatch a window event that
    // <RateLimitBanner /> in DashboardLayout picks up.
    if (status === 429 && typeof window !== 'undefined') {
      const detail = error.response?.data?.detail
      const detailStr = typeof detail === 'string'
        ? detail
        : (detail && typeof detail === 'object' && (detail as { message?: string }).message) || ''
      const retryAfterRaw = error.response?.headers?.['retry-after']
      const retryAfterSec = retryAfterRaw ? Number(retryAfterRaw) : null
      const endpoint: string = (error.config?.url as string) || ''
      // Skip the global banner for /auth/login since the login page renders
      // its own "too many attempts, wait Xs" message inline.
      if (!endpoint.startsWith('/auth/login')) {
        try {
          window.dispatchEvent(
            new CustomEvent('ps:rate-limited', {
              detail: {
                endpoint,
                message: detailStr || 'You hit a rate limit. Slow down or upgrade for higher quotas.',
                retryAfterSec: Number.isFinite(retryAfterSec) ? retryAfterSec : null,
              },
            }),
          )
        } catch {
          // CustomEvent unsupported in this runtime; nothing to do.
        }
      }
    }

    // J5-402 (v227): mid-session quota exhaustion / plan gate. The backend's
    // standard 402 payload is detail.error = 'plan_limit_exceeded' (monthly
    // quotas + row-count caps: briefings / DD reports / alert rules / seats)
    // or 'plan_required' (tier gates), carrying current_plan / limit / usage
    // and, where applicable, the build_upgrade_cta fragment. Reject with a
    // typed QuotaExceededError so error surfaces (ErrorState) can render a
    // quota-specific state with an upgrade CTA instead of a generic failure.
    // A 402 whose payload is NOT the standard shape falls through untouched,
    // so routes that should never 402 keep their generic surface. Unlike
    // 401/403 there is deliberately no redirect here — the session is valid,
    // the user just hit a cap — so no redirect loop is possible.
    if (status === 402) {
      const quota = parseQuotaExceededPayload(error.response?.data)
      if (quota) {
        return Promise.reject(new QuotaExceededError(quota, error))
      }
    }

    if ((status === 401 || status === 403) && typeof window !== 'undefined') {
      const token = localStorage.getItem('access_token')
      const detail = error.response?.data?.detail
      const onAuthPage = ['/login', '/signup', '/verify-email', '/auth/google'].some((p) => window.location.pathname.startsWith(p))
      // Do not redirect on email-not-verified - the signup flow handles that
      if (detail === 'EMAIL_NOT_VERIFIED') return Promise.reject(error)

      // B3: structured 401 body from deps.get_current_user.
      // detail = {"reason": "session_revoked" | "upgrade_required", "message": "..."}
      const reason: string | undefined =
        (typeof detail === 'object' && detail && (detail as { reason?: string }).reason) || undefined

      if (!onAuthPage && (status === 401 || !token)) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')
        const qs =
          reason === 'session_revoked'
            ? '?session_ended=elsewhere'
            : reason === 'upgrade_required'
            ? '?session_ended=upgrade'
            : ''
        window.location.href = `/login${qs}`
      }
    }
    // 5xx surfaces are real backend failures; route them through the
    // observability shim so the env-gated tracker picks them up.
    // 4xx (other than 401/403 handled above) are caller mistakes; we
    // skip them to avoid burning the beacon quota on validation noise.
    if (typeof status === 'number' && status >= 500) {
      try {
        captureException(error, {
          status,
          endpoint: (error.config?.url as string) || '',
          source: 'api/interceptor',
        })
      } catch {
        // Observability must never break the request error path.
      }
    }
    return Promise.reject(error)
  }
)

// ─── Auth ─────────────────────────────────────────────────────────────────────

// /auth/login + /auth/google return one of two discriminated shapes:
//   {status: "ok", access_token, token_type}        // happy path
//   {status: "session_conflict", message, active_sessions: [...]}
// The login form handles both: the conflict shape pops the takeover
// dialog, the ok shape stores the token like before.
export type ActiveSessionInfo = {
  id: number
  device: string
  fingerprint: string | null
  last_active: string | null
  created_at: string | null
  expires_at: string | null
  is_current?: boolean
}

export type LoginOk = {
  status: 'ok'
  access_token: string
  token_type: string
  // SEC-2FA-3: true when the user's org mandates 2FA but they have not yet
  // enrolled a second factor. The client routes them into 2FA setup.
  must_enroll_2fa?: boolean
}

export type LoginConflict = {
  status: 'session_conflict'
  message: string
  active_sessions: ActiveSessionInfo[]
  // /auth/google only: server-issued token the client re-presents with
  // force_takeover=true so we don't have to re-exchange Google's
  // one-time-use authorization code (which would 400 with
  // `invalid_grant`).
  session_handshake_token?: string
}

// SEC-2FA-3 (v153): a 2FA-enabled account must clear its second factor at
// login. The password path re-POSTs the same credentials plus totp_code; the
// Google path cannot re-exchange the one-time-use code, so it carries a
// server-issued totp_handshake_token the client re-presents with the code.
export type Login2faRequired = {
  status: '2fa_required'
  message: string
  totp_handshake_token?: string
}

export type LoginResponse = LoginOk | LoginConflict | Login2faRequired

export async function login(
  data: LoginRequest & {
    force_takeover?: boolean
    // NB-F195: Cloudflare Turnstile verification token. Sent both as a
    // body field (so FastAPI Pydantic models can pick it up) and as the
    // `cf-turnstile-response` header (so a backend middleware can verify
    // before reaching the route). Backend only enforces if its
    // TURNSTILE_SECRET env is set; otherwise the field is ignored.
    turnstile_token?: string
    // SEC-2FA-3: the second-factor code re-submitted after a 2fa_required
    // response (the password path re-POSTs the same credentials + code).
    totp_code?: string
  }
): Promise<LoginResponse> {
  const headers: Record<string, string> = {}
  if (data.turnstile_token) {
    headers['cf-turnstile-response'] = data.turnstile_token
  }
  const res = await api.post<LoginResponse>('/auth/login', data, { headers })
  return res.data
}

// SEC-AUTH: fetch a server-issued OAuth `state` value before redirecting to
// Google. The backend stores/validates it (behind GOOGLE_OAUTH_STATE_ENFORCE)
// to protect the callback against CSRF. Returns null if the backend does not
// issue one (older image / feature disabled) so the caller can degrade to the
// legacy no-state redirect rather than blocking sign-in.
export async function getGoogleOAuthState(): Promise<string | null> {
  try {
    const res = await api.get<{ state?: string | null }>('/auth/google/state')
    return res.data?.state ?? null
  } catch {
    return null
  }
}

// googleAuth supports three modes:
//   (1) fresh code exchange: pass code + redirectUri, omit the handshakes.
//   (2) takeover retry: pass handshakeToken + forceTakeover=true; omit code.
//   (3) SEC-2FA-3 2FA retry: pass totpHandshakeToken + totpCode (omit code);
//       completes an OAuth login that required a second factor.
export async function googleAuth(
  code: string | null,
  redirectUri: string | null,
  forceTakeover = false,
  handshakeToken: string | null = null,
  opts: { totpHandshakeToken?: string | null; totpCode?: string | null } = {},
): Promise<LoginResponse> {
  const body: Record<string, unknown> = {
    force_takeover: forceTakeover,
  }
  if (opts.totpHandshakeToken) {
    // 2FA retry: re-present the challenge handshake + the entered code. No
    // Google code (one-time-use) and no force_takeover yet.
    body.totp_handshake_token = opts.totpHandshakeToken
    body.totp_code = opts.totpCode ?? ''
  } else if (handshakeToken) {
    body.session_handshake_token = handshakeToken
  } else {
    body.code = code
    body.redirect_uri = redirectUri
  }
  const res = await api.post<LoginResponse>('/auth/google', body)
  return res.data
}

// Session-management endpoints.
export async function listSessions(): Promise<{ sessions: ActiveSessionInfo[] }> {
  const res = await api.get<{ sessions: ActiveSessionInfo[] }>('/auth/sessions')
  return res.data
}

export async function revokeSession(sessionId: number): Promise<void> {
  await api.post(`/auth/sessions/${sessionId}/revoke`)
}

export async function revokeOtherSessions(): Promise<{ revoked: number }> {
  const res = await api.post<{ status: string; revoked: number }>(
    '/auth/sessions/revoke-others',
  )
  return { revoked: res.data.revoked }
}

export async function logoutSession(): Promise<void> {
  try {
    await api.post('/auth/logout')
  } catch {
    // Logout is best-effort, even if the call fails (e.g. token already
    // revoked elsewhere), the client still nukes localStorage.
  }
}

export async function heartbeatSession(): Promise<void> {
  try {
    await api.post('/auth/heartbeat')
  } catch {
    // Silent, network blips happen and the next heartbeat will catch up.
  }
}

// SEC-AUTH: extend an active session within the backend's 30-day cap. The
// backend re-issues an access token and slides the session expiry forward;
// this call swaps in the fresh token so a long-lived tab stays signed in
// without forcing a re-login. Best-effort: a failure just means the current
// token keeps running until it expires (at which point the 401 interceptor
// routes to /login). Returns true when a new token was persisted.
export async function refreshSession(): Promise<boolean> {
  try {
    const res = await api.post<{ access_token?: string; token_type?: string }>(
      '/auth/refresh',
    )
    const token = res.data?.access_token
    if (token && typeof window !== 'undefined') {
      localStorage.setItem('access_token', token)
      return true
    }
    return false
  } catch {
    return false
  }
}

export async function signup(
  data: SignupRequest & { turnstile_token?: string },
): Promise<User> {
  // NB-F195: same dual-channel (body + header) Turnstile pattern as login.
  const headers: Record<string, string> = {}
  if (data.turnstile_token) {
    headers['cf-turnstile-response'] = data.turnstile_token
  }
  const res = await api.post<User>('/auth/register', data, { headers })
  return res.data
}

export async function sendOTP(email: string): Promise<void> {
  await api.post('/auth/send-otp', { email })
}

export async function verifyOTP(email: string, code: string): Promise<AuthToken> {
  const res = await api.post<AuthToken>('/auth/verify-otp', { email, code })
  return res.data
}

// NB-F322: typed password-reset helpers. Wraps the backend
// /auth/forgot-password (always 204, no enumeration) and /auth/reset-password
// (200 ok | 400 invalid|expired|already-used). Both endpoints are rate-limited
// 3/min per IP; surface 429 to the caller so it can render Retry-After.
export async function requestPasswordReset(
  email: string,
  opts?: { turnstileToken?: string },
): Promise<void> {
  const headers: Record<string, string> = {}
  if (opts?.turnstileToken) {
    headers['cf-turnstile-response'] = opts.turnstileToken
  }
  await api.post(
    '/auth/forgot-password',
    { email, turnstile_token: opts?.turnstileToken || undefined },
    { headers },
  )
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<void> {
  await api.post('/auth/reset-password', {
    token,
    new_password: newPassword,
  })
}

export async function getMe(tokenOverride?: string): Promise<User> {
  // Optional override lets the sign-in flow fetch the user without first
  // writing the token to localStorage, closing the half-state window
  // where the token is committed but `getMe` fails (or the tab closes),
  // leaving a phantom logged-in state with no user object.
  if (tokenOverride) {
    const res = await api.get<User>('/auth/me', {
      headers: { Authorization: `Bearer ${tokenOverride}` },
    })
    return res.data
  }
  const res = await api.get<User>('/auth/me')
  return res.data
}

// ─── Billing (Polar.sh) ───────────────────────────────────────────────────────

export type CheckoutPlan = 'starter' | 'pro' | 'business' | 'scale'
export type BillingCycle = 'monthly' | 'annual'

// AUDIT-58 (classes 5 + 6): discount codes, checkout prefill, self-serve
// refund. These call new server-side endpoints under the same /billing
// prefix as the existing checkout/portal calls. All validation (discount
// legitimacy, the 7-day refund window) is enforced on the backend; the
// client never trusts its own computed prices.

// A Polar billing address for checkout prefill. Presetting it makes Polar
// require a billing address from the customer (require_billing_address).
export interface BillingAddress {
  line1?: string
  line2?: string
  city?: string
  state?: string
  postal_code?: string
  // ISO 3166-1 alpha-2 country code (e.g. "US"), per Polar.
  country?: string
}

export interface CheckoutOptions {
  discountCode?: string
  fullName?: string
  company?: string
  address?: BillingAddress
}

// Response from POST /billing/validate-discount. The backend lists the
// org's Polar discounts and matches `code` case-insensitively, then
// computes the original/final price for the selected plan + interval.
export type DiscountValidation =
  | {
      valid: true
      code: string
      kind: 'percent' | 'fixed'
      percent_off?: number
      amount_off_cents?: number
      original_cents: number
      final_cents: number
      description: string
    }
  | {
      valid: false
      reason: string
    }

export interface RefundResult {
  refunded: boolean
  eligible: boolean
  refund_id?: string
  amount_cents?: number
  reason?: string
}

export interface RefundStatus {
  eligible: boolean
  within_window: boolean
  days_remaining: number
  first_charged_at?: string
}

/**
 * Validate a discount code server-side against Polar for a given plan +
 * interval. The backend returns the original and final price in cents so
 * the page can render the struck-through original next to the final.
 *
 * Returns {valid:false, reason} for unknown/expired/exhausted codes; the
 * caller surfaces `reason` inline. Never throws on a bad code (the
 * backend answers 200 with valid:false).
 */
export async function validateDiscount(
  code: string,
  plan: CheckoutPlan,
  billing: BillingCycle,
): Promise<DiscountValidation> {
  const res = await api.post<DiscountValidation>('/billing/validate-discount', {
    code,
    plan,
    billing,
  })
  return res.data
}

/**
 * Create a Polar checkout session.
 *
 * AUDIT-58: extended to carry an optional discount code (re-validated and
 * resolved to a Polar discount_id server-side), the customer name +
 * company, and a billing address for prefill. Backwards-compatible: a
 * plain `createCheckout(plan, billing)` call still works (the third arg
 * is optional and empty fields are omitted from the request body).
 */
export async function createCheckout(
  plan: CheckoutPlan,
  billing: BillingCycle,
  opts?: CheckoutOptions,
): Promise<{ checkout_url: string }> {
  const body: {
    plan: CheckoutPlan
    billing: BillingCycle
    discount_code?: string
    full_name?: string
    company?: string
    address?: BillingAddress
  } = { plan, billing }

  if (opts?.discountCode) body.discount_code = opts.discountCode
  if (opts?.fullName) body.full_name = opts.fullName
  if (opts?.company) body.company = opts.company
  if (opts?.address) {
    // Strip empty fields so we never send a half-empty address object
    // that would make Polar require a fully-typed address with blanks.
    const a = opts.address
    const cleaned: BillingAddress = {}
    if (a.line1) cleaned.line1 = a.line1
    if (a.line2) cleaned.line2 = a.line2
    if (a.city) cleaned.city = a.city
    if (a.state) cleaned.state = a.state
    if (a.postal_code) cleaned.postal_code = a.postal_code
    if (a.country) cleaned.country = a.country
    if (Object.keys(cleaned).length > 0) body.address = cleaned
  }

  const res = await api.post<{ checkout_url: string }>('/billing/checkout', body)
  return res.data
}

/**
 * Request a self-serve full refund of the latest paid order. The backend
 * finds the most-recent paid Polar order, enforces the 7-day-since-first-
 * charge window server-side, and issues a full refund via Polar. The
 * order.refunded webhook then downgrades the tier and sends the refund
 * email. Returns {refunded:false, eligible:false, reason} when there is
 * no paid order or the window has passed.
 */
export async function requestRefund(reason?: string): Promise<RefundResult> {
  const res = await api.post<RefundResult>('/billing/request-refund', {
    reason: reason || undefined,
  })
  return res.data
}

/**
 * Live-query Polar (via the backend) for whether the user is inside the
 * 7-day self-serve refund window, used to show/enable the refund button.
 */
export async function getRefundStatus(): Promise<RefundStatus> {
  const res = await api.get<RefundStatus>('/billing/refund-status')
  return res.data
}

export async function openCustomerPortal(): Promise<void> {
  const res = await api.post<{ portal_url: string }>('/billing/portal')
  if (typeof window !== 'undefined' && res.data?.portal_url) {
    window.location.href = res.data.portal_url
  }
}

// Self-serve cancel of an in-progress 14-day trial (backs the billing-page
// "Cancel trial" action). Idempotent on the backend: { canceled: true } once,
// { canceled: false, already_ended: true } or { ..., reason } otherwise.
export async function cancelTrial(): Promise<{
  canceled: boolean
  reason?: string
  already_ended?: boolean
}> {
  const res = await api.post('/billing/cancel-trial')
  return res.data
}

// ─── Overview ─────────────────────────────────────────────────────────────────

export async function getOverviewSummary(): Promise<import('@/types').OverviewSummary> {
  const res = await api.get('/overview/summary')
  return res.data
}

// G15 — ?weeks=8|12|26 widens the trend window. The default (8) sends no
// param so the request stays byte-identical to the pre-G15 one; a backend
// that does not know the param simply serves its 8-week default and the
// cards size themselves from the served buckets.
export async function getOverviewTrends(
  weeks?: import('@/types').TrendWindowWeeks,
): Promise<import('@/types').OverviewTrends> {
  const res = await api.get('/overview/trends', {
    params: weeks && weeks !== 8 ? { weeks } : undefined,
  })
  return res.data
}

export async function getOverviewTopSignals(
  limit = 7,
): Promise<import('@/types').OverviewTopSignals> {
  const res = await api.get('/overview/top-signals', { params: { limit } })
  return res.data
}

// DASH-VAL-2 — per-company cross-module narrative synthesis (the live
// narrative_events engine) scoped to the watchlist + clamped to the tier
// history window. Backend: GET /overview/cross-signals (rides image i440). The
// CrossSignalCard renders nothing until the endpoint exists, so shipping the
// frontend ahead of the backend is safe.
export async function getOverviewCrossSignals(
  limit = 8,
  perCompany = 4,
  module?: string,
): Promise<import('@/types').OverviewCrossSignals> {
  const res = await api.get('/overview/cross-signals', {
    // DASH-VAL-2b — `module` (a signal-page slug) restricts to that module's
    // cross-module stories for the per-signal-page card; omit it for the global
    // overview card.
    params: { limit, per_company: perCompany, ...(module ? { module } : {}) },
  })
  return res.data
}

// ─── Companies ────────────────────────────────────────────────────────────────

export async function getCompanies(params?: {
  page?: number
  page_size?: number
  industry?: string
  search?: string
  has_plans?: boolean
  country?: string
}): Promise<PaginatedCompanies> {
  const res = await api.get<PaginatedCompanies>('/companies', { params })
  return res.data
}

export async function getCompanyNames(): Promise<Record<string, { name: string; domain: string }>> {
  const res = await api.get('/companies/names')
  return res.data
}

export async function autocompleteCompanies(q: string): Promise<
  { id: number; name: string; logo_url: string | null; industry: string | null; domain: string; module_data_count: number; enrichment_status: string; last_enriched_at: string | null; status: string; deactivated_at?: string | null }[]
> {
  const res = await api.get('/companies/autocomplete', { params: { q } })
  return res.data
}

export async function getLastKnownData(companyId: number): Promise<Record<string, unknown>> {
  const res = await api.get(`/companies/${companyId}/last-known-data`)
  return res.data
}

export async function enrichCompany(companyId: number): Promise<{ status: string; message: string; has_website: boolean; is_active?: boolean; module_data_count?: number; enrichment_status?: string; last_enriched_at?: string | null }> {
  const res = await api.post(`/companies/${companyId}/enrich`)
  return res.data
}

export async function getCompany(id: number): Promise<Company> {
  const res = await api.get<Company>(`/companies/${id}`)
  return res.data
}

export async function createCompany(data: {
  name: string
  website: string
  industry: string
  description?: string
}): Promise<Company> {
  const res = await api.post<Company>('/companies', data)
  return res.data
}

export async function trackCompany(url: string): Promise<{
  status: string; message: string; company_id?: number; company_name?: string
}> {
  const res = await api.post('/companies/track', null, { params: { url } })
  return res.data
}

export async function requestCompany(searchTerm: string, websiteUrl?: string): Promise<{
  status: string; attempt?: number; message: string; company_id?: number; company_name?: string
}> {
  const params: Record<string, string> = { search_term: searchTerm }
  if (websiteUrl) params.website_url = websiteUrl
  const res = await api.post('/companies/request-company', null, { params })
  return res.data
}

// BRIEF_H: free-text search → LLM gate. Hits POST /search/classify-or-add and
// returns a kind-tagged response the Topbar can switch on.
export type ClassifyKind =
  | 'existing'
  | 'added'
  | 'not_saas'
  | 'unclear'
  | 'invalid'
  | 'ratelimit'
  | 'error'

export interface ClassifyOrAddResponse {
  kind: ClassifyKind
  message?: string
  company_id?: number
  name?: string
  website?: string
  bucket?: 'daily' | 'weekly'
  reason?: string
}

export async function classifyOrAddCompany(
  name: string,
  website?: string,
): Promise<ClassifyOrAddResponse> {
  const body: { name: string; website?: string } = { name }
  if (website) body.website = website
  const res = await api.post<ClassifyOrAddResponse>('/search/classify-or-add', body)
  return res.data
}

// ─── Per-module trends (9584) ─────────────────────────────────────────────────

// GET /datasets/{module}/trends — trend direction for a module page's headline
// counts. `module` MUST be the canonical underscore machine key
// ("hiring_signals", "funding_signals", "product_launches",
// "leadership_intelligence", ...): the short marketing segments are swallowed
// by the fixed dataset routers registered first (/datasets/hiring/trends is a
// DIFFERENT legacy endpoint; /datasets/pricing/trends 422s). `companyId`
// optionally scopes to one company; omitted → the caller's effective
// watchlist. The endpoint ships in a parallel backend roll — callers must
// treat every response field as optional and degrade to "no badge" on 404 or
// a shape mismatch (see ModuleTrendSummary).
export async function getModuleTrends(
  module: string,
  companyId?: number,
): Promise<import('@/types').ModuleTrendSummary> {
  const res = await api.get(`/datasets/${module}/trends`, {
    params: companyId != null ? { company_id: companyId } : undefined,
  })
  return res.data
}

// ─── Pricing Intelligence ─────────────────────────────────────────────────────

export async function getPricing(params?: {
  company_id?: number
  billing_frequency?: string
  min_price?: number
  max_price?: number
  scraped_after?: string
  scraped_before?: string
  limit?: number
  offset?: number
  // G14: server-side watchlist scope. A backend that predates the param
  // ignores it, so callers also filter client-side by the effective
  // watchlist ids (lib/signalScope.ts).
  watchlist_only?: boolean
}): Promise<PricingPlan[]> {
  const res = await api.get<PricingPlan[]>('/datasets/pricing', { params })
  return res.data
}

export async function getCompanyPricing(companyId: number): Promise<PricingPlan[]> {
  const res = await api.get<PricingPlan[]>(`/datasets/pricing/${companyId}`)
  return res.data
}

export async function getPricingHistory(planId: number, limit?: number): Promise<PricingHistory[]> {
  const res = await api.get<PricingHistory[]>(`/datasets/pricing/history/${planId}`, {
    params: { limit },
  })
  return res.data
}

export async function getRecentChanges(days?: number): Promise<PricingHistory[]> {
  const res = await api.get<PricingHistory[]>('/datasets/pricing/changes', {
    params: { days },
  })
  return res.data
}

export async function compareCompanies(companyIds: number[]): Promise<CompanyComparisonItem[]> {
  const res = await api.get<CompanyComparisonItem[]>('/datasets/pricing/compare', {
    params: { company_ids: companyIds.join(',') },
  })
  return res.data
}

// ─── Analytics ────────────────────────────────────────────────────────────────

export async function getMarketSummary(): Promise<MarketSummary> {
  const res = await api.get<MarketSummary>('/analytics/market-summary')
  return res.data
}

export async function getPricingTrends(days?: number): Promise<PricingTrend[]> {
  const res = await api.get<PricingTrend[]>('/analytics/pricing-trends', { params: { days } })
  return res.data
}

export async function getPriceDistribution(): Promise<PriceDistributionBucket[]> {
  const res = await api.get<PriceDistributionBucket[]>('/analytics/price-distribution')
  return res.data
}

export async function getFeatureFrequency(limit?: number): Promise<FeatureFrequency[]> {
  const res = await api.get<FeatureFrequency[]>('/analytics/feature-frequency', {
    params: { limit },
  })
  return res.data
}

// ─── Alerts ───────────────────────────────────────────────────────────────────

export async function getAlerts(): Promise<Alert[]> {
  const res = await api.get<Alert[]>('/alerts')
  return res.data
}

export async function subscribeToAlerts(data: AlertSubscribeRequest): Promise<Alert> {
  const res = await api.post<Alert>('/alerts/subscribe', data)
  return res.data
}

export async function deleteAlert(alertId: number): Promise<void> {
  // The legacy router now expects company_id as the path param (the
  // schema kept `id` echoing `company_id` for backwards compat). New
  // code should call deleteWatchlistSubscription instead.
  await api.delete(`/alerts/${alertId}`)
}

// ─── In-app notification inbox (10111 FE-half) ────────────────────────────
//
// GET /notifications → { items, unread_count } (limit hard-clamped [1,100],
// stable created_at DESC + id DESC ordering; rate limit 60/min). The shape
// ships in a parallel backend roll, so callers MUST feature-detect via
// lib/notificationsInbox.isInboxListShape and degrade to "no badge / not
// available yet" on a 404 or a legacy payload on the same path.

export async function getNotifications(params?: {
  limit?: number
  offset?: number
  unread_only?: boolean
}): Promise<import('@/types').InboxNotificationsList> {
  const res = await api.get('/notifications', { params })
  return res.data
}

// POST /notifications/{id}/read — 404 for a cross-user or unknown row,
// { marked: 0 } no-op success when it was already read.
export async function markNotificationRead(id: number): Promise<{ marked: number }> {
  const res = await api.post(`/notifications/${id}/read`)
  return res.data
}

// POST /notifications/read-all — rate limit 10/min; callers should disable
// the affordance while a call is in flight rather than retry-storm.
export async function markAllNotificationsRead(): Promise<{ marked: number }> {
  const res = await api.post('/notifications/read-all')
  return res.data
}

// ─── Watchlist ────────────────────────────────────────────────────────────

export async function getEffectiveWatchlist(): Promise<EffectiveWatchlist> {
  const res = await api.get<EffectiveWatchlist>('/watchlist/effective')
  return res.data
}

export async function addCompanyToWorkspaceWatchlist(
  company_id: number,
): Promise<WatchlistCompany> {
  const res = await api.post<WatchlistCompany>('/watchlist/companies', { company_id })
  return res.data
}

export async function removeCompanyFromWorkspaceWatchlist(
  company_id: number,
): Promise<void> {
  await api.delete(`/watchlist/companies/${company_id}`)
}

export async function setWatchlistOverride(
  company_id: number,
  action: 'add' | 'hide' = 'add',
): Promise<WatchlistCompany> {
  const res = await api.post<WatchlistCompany>('/watchlist/overrides', {
    company_id,
    action,
  })
  return res.data
}

export async function clearWatchlistOverride(company_id: number): Promise<void> {
  await api.delete(`/watchlist/overrides/${company_id}`)
}

// Convenience: role-aware "subscribe me to this company" that picks the
// right table on the backend. Use this from card-level CTAs that don't
// know whether the caller is org-writer or not.
export async function subscribeToWatchlist(company_id: number): Promise<WatchlistCompany> {
  const res = await api.post<WatchlistCompany>('/watchlist/subscribe', { company_id })
  return res.data
}

export async function deleteWatchlistSubscription(company_id: number): Promise<void> {
  await api.delete(`/watchlist/subscribe/${company_id}`)
}

// ─── Cycles (Phase 5 daily-snapshot pointer) ──────────────────────────────

export async function getServedCycle(): Promise<ServedCycle> {
  const res = await api.get<ServedCycle>('/cycles/served')
  return res.data
}

// ─── Hiring Signals ───────────────────────────────────────────────────────────

export async function getCompanyHiring(companyId: number): Promise<HiringData> {
  const res = await api.get<HiringData>(`/datasets/hiring/${companyId}`)
  return res.data
}

export async function getHiringHistory(
  companyId: number,
  limit?: number
): Promise<HeadcountSnapshot[]> {
  const res = await api.get<HeadcountSnapshot[]>(`/datasets/hiring/${companyId}/history`, {
    params: { limit },
  })
  return res.data
}

export async function getHiringTrends(opts?: { watchlistOnly?: boolean }): Promise<HiringTrends> {
  // G14: watchlist_only asks the backend to scope the aggregation. The
  // response echoes scope: 'watchlist' when honored; an older backend
  // ignores the param and omits the echo (feature detection).
  const res = await api.get<HiringTrends>('/datasets/hiring/trends', {
    params: { watchlist_only: opts?.watchlistOnly ? true : undefined },
  })
  return res.data
}

// ─── Funding Signals ──────────────────────────────────────────────────────────

export async function getCompanyFunding(companyId: number): Promise<FundingRound[]> {
  const res = await api.get<FundingRound[]>(`/datasets/funding/${companyId}`)
  return res.data
}

export async function getRecentFundingRounds(
  days?: number,
  opts?: { watchlistOnly?: boolean; limit?: number },
): Promise<FundingRound[]> {
  const res = await api.get<FundingRound[]>('/datasets/funding/recent', {
    params: {
      days,
      limit: opts?.limit,
      watchlist_only: opts?.watchlistOnly ? true : undefined,
    },
  })
  return res.data
}

export async function getTopInvestors(
  limit?: number
): Promise<{ investor: string; deal_count: number }[]> {
  const res = await api.get('/datasets/funding/investors', { params: { limit } })
  return res.data
}

// ─── Product Launches ─────────────────────────────────────────────────────────

export async function getCompanyLaunches(
  companyId: number,
  entryType?: string
): Promise<ChangelogEntry[]> {
  const res = await api.get<ChangelogEntry[]>(`/datasets/launches/${companyId}`, {
    params: { entry_type: entryType },
  })
  return res.data
}

export async function getRecentLaunches(
  days?: number,
  opts?: { watchlistOnly?: boolean; limit?: number },
): Promise<ChangelogEntry[]> {
  const res = await api.get<ChangelogEntry[]>('/datasets/launches/recent', {
    params: {
      days,
      limit: opts?.limit,
      watchlist_only: opts?.watchlistOnly ? true : undefined,
    },
  })
  return res.data
}

// ─── Customer Sentiment ───────────────────────────────────────────────────────

export async function getCompanySentiment(
  companyId: number
): Promise<{ overall_avg_rating?: number; platforms: ReviewAggregate[] }> {
  const res = await api.get(`/datasets/sentiment/${companyId}`)
  return res.data
}

// Rating-over-time series from review_aggregate_snapshots. Newest-first,
// tier-windowed server-side (same shape as getHiringHistory).
export async function getCompanySentimentHistory(
  companyId: number,
  limit?: number,
): Promise<SentimentHistoryPoint[]> {
  const res = await api.get<SentimentHistoryPoint[]>(`/datasets/sentiment/${companyId}/history`, {
    params: { limit },
  })
  return res.data
}

export async function getSentimentLeaderboard(
  limit?: number,
  opts?: { watchlistOnly?: boolean },
): Promise<SentimentLeaderboardItem[]> {
  const res = await api.get<SentimentLeaderboardItem[]>('/datasets/sentiment/leaderboard', {
    params: { limit, watchlist_only: opts?.watchlistOnly ? true : undefined },
  })
  return res.data
}

export async function compareSentiment(companyIds: number[]): Promise<SentimentLeaderboardItem[]> {
  const res = await api.get<SentimentLeaderboardItem[]>('/datasets/sentiment/compare', {
    params: { company_ids: companyIds.join(',') },
  })
  return res.data
}

// ─── Tech Stack ───────────────────────────────────────────────────────────────

export async function getCompanyTechStack(companyId: number): Promise<TechStackData> {
  const res = await api.get<TechStackData>(`/datasets/tech-stack/${companyId}`)
  return res.data
}

export async function getMarketTechStack(opts?: { watchlistOnly?: boolean }): Promise<{
  top_technologies: { name: string; count: number }[]
  by_category: Record<string, { name: string; count: number }[]>
  // G14: present (= 'watchlist') only when the backend honored
  // watchlist_only; older backends omit it (feature detection).
  scope?: 'watchlist'
}> {
  const res = await api.get('/datasets/tech-stack/market', {
    params: { watchlist_only: opts?.watchlistOnly ? true : undefined },
  })
  return res.data
}

// GAP 4 — drill a technology back to the companies that use it. Mirrors the
// /market client fn: passes watchlist_only only when scoped (feature
// detection), and the backend echoes scope: 'watchlist' when it honors it.
export interface TechStackCompany {
  company_id: number
  company_name: string | null
  category: string | null
  confidence: string | null
  source: string | null
  snapshotted_at: string | null
}

export async function getCompaniesByTechnology(
  name: string,
  opts?: { watchlistOnly?: boolean; limit?: number },
): Promise<{
  name: string
  companies: TechStackCompany[]
  total: number
  scope?: 'watchlist'
}> {
  const res = await api.get('/datasets/tech-stack/by-technology', {
    params: {
      name,
      limit: opts?.limit,
      watchlist_only: opts?.watchlistOnly ? true : undefined,
    },
  })
  return res.data
}

// ─── Leadership ───────────────────────────────────────────────────────────────

export async function getCompanyLeadership(companyId: number): Promise<{
  current_team: { full_name: string; title?: string }[]
  recent_changes: LeadershipChange[]
}> {
  const res = await api.get(`/datasets/leadership/${companyId}`)
  return res.data
}

export async function getLeadershipChanges(
  days?: number,
  changeType?: string,
  opts?: { watchlistOnly?: boolean; limit?: number },
): Promise<LeadershipChange[]> {
  const res = await api.get<LeadershipChange[]>('/datasets/leadership/changes', {
    params: {
      days,
      change_type: changeType,
      limit: opts?.limit,
      watchlist_only: opts?.watchlistOnly ? true : undefined,
    },
  })
  return res.data
}

// ─── Positioning Intelligence ─────────────────────────────────────────────────

export async function getPositioningChanges(params?: {
  company_id?: number
  classification?: string
  severity?: string
  page_type?: string
  days?: number
  limit?: number
  offset?: number
  // G14: server-side watchlist scope (older backends ignore it; callers
  // also filter client-side).
  watchlist_only?: boolean
}): Promise<PositioningChange[]> {
  const res = await api.get<PositioningChange[]>('/datasets/positioning', {
    params,
  })
  return res.data
}

export async function getCompanyPositioning(
  companyId: number,
  limit?: number
): Promise<PositioningSummary> {
  const res = await api.get<PositioningSummary>(`/datasets/positioning/${companyId}`, {
    params: { limit },
  })
  return res.data
}

export async function getPositioningChange(changeId: number): Promise<PositioningChange> {
  const res = await api.get<PositioningChange>(`/datasets/positioning/changes/${changeId}`)
  return res.data
}

export async function getPositioningSnapshots(
  companyId: number,
  pageType?: string,
  limit?: number
): Promise<PositioningSnapshot[]> {
  const res = await api.get<PositioningSnapshot[]>(
    `/datasets/positioning/snapshots/${companyId}`,
    { params: { page_type: pageType, limit } }
  )
  return res.data
}

// ─── Company-detail modules (MAKE-REAL, v101) ─────────────────────────────────
// Six per-company dataset surfaces rendered inside /dashboard/companies/[id].
// Each mirrors getCompanyPricing: a single company-scoped GET that the backend
// guarantees to answer empty-safe (zeroed / has_data:false), never a 404/500.

export async function getCompanyComplianceCerts(
  companyId: number,
): Promise<CompanyComplianceCerts> {
  const res = await api.get<CompanyComplianceCerts>(`/datasets/compliance-certs/${companyId}`)
  return res.data
}

export async function getCompanyCustomerLogos(
  companyId: number,
): Promise<CompanyCustomerLogos> {
  const res = await api.get<CompanyCustomerLogos>(`/datasets/customer-logos/${companyId}`)
  return res.data
}

export async function getCompanyIntegrationIntelligence(
  companyId: number,
): Promise<CompanyIntegrationIntelligence> {
  const res = await api.get<CompanyIntegrationIntelligence>(
    `/datasets/integration-intelligence/${companyId}`,
  )
  return res.data
}

export async function getCompanyEmployeeReviews(
  companyId: number,
): Promise<EmployeeReviewsResponse> {
  const res = await api.get<EmployeeReviewsResponse>(`/datasets/employee-reviews/${companyId}`)
  return res.data
}

export async function getCompanyEmployeeDepth(
  companyId: number,
): Promise<EmployeeDepthResponse> {
  const res = await api.get<EmployeeDepthResponse>(`/datasets/employee-depth/${companyId}`)
  return res.data
}

export async function getCompanyWorkforceImmigration(
  companyId: number,
): Promise<WorkforceImmigrationResponse> {
  const res = await api.get<WorkforceImmigrationResponse>(
    `/datasets/workforce-immigration/${companyId}`,
  )
  return res.data
}

// ─── Signals ─────────────────────────────────────────────────────────────────

// ─── Due Diligence ────────────────────────────────────────────────────────────

export async function generateDDReport(companyId: number): Promise<DDReport> {
  const res = await api.post<DDReport>(`/due-diligence/${companyId}`)
  return res.data
}

// AUD-DASH (v153): proactive monthly DD-report quota (read-only peek, no
// increment) so the UI can show "X of N used" before the user hits the cap.
export type DDQuota = {
  status: string
  used: number
  limit: number | null // null => unlimited (fair use)
  remaining: number | null
  period_end: string
}

export async function getDDQuota(): Promise<DDQuota | null> {
  try {
    const res = await api.get<DDQuota>('/due-diligence/quota')
    return res.data
  } catch {
    // A quota read must never block the page; the cap is still enforced
    // server-side on generate.
    return null
  }
}

export async function getLatestDDReport(companyId: number): Promise<{
  id: number
  company_id: number
  acquisition_score?: number
  risk_score?: number
  growth_score?: number
  generated_at?: string
  report_data: DDReport
} | null> {
  // Backend wraps the payload as `{ status, data }` (data is null when no
  // report exists). Unwrap the inner object so callers read report_data off
  // the actual report; reading res.data directly always yielded undefined.
  const res = await api.get(`/due-diligence/${companyId}/latest`)
  return res.data?.data ?? null
}

// Server returns a PDF stream from GET /due-diligence/{id}/latest/pdf;
// turn it into a browser download. Mirrors downloadReportPdf in
// @/lib/api/reports.
export async function downloadDDReportPdf(companyId: number): Promise<void> {
  const res = await api.get(`/due-diligence/${companyId}/latest/pdf`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(
    new Blob([res.data], { type: 'application/pdf' }),
  )
  const a = document.createElement('a')
  a.href = url
  a.download = `due-diligence-${companyId}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

export async function getAcquisitionTargets(
  minScore?: number,
  limit?: number
): Promise<AcquisitionTarget[]> {
  const res = await api.get('/due-diligence/acquisition-targets', {
    params: { min_score: minScore, limit },
  })
  if (res.data && res.data.status === 'empty') return []
  return res.data?.data ?? res.data ?? []
}

// ─── Module Insights ─────────────────────────────────────────────────────────

export async function getModuleInsights(module: string): Promise<{ module: string; insights: string[]; generated_at?: string }> {
  const res = await api.get('/overview/module-insights', { params: { module } })
  return res.data
}

// ─── API Keys ─────────────────────────────────────────────────────────────────

export async function listApiKeys(): Promise<import('@/types').ApiKey[]> {
  const res = await api.get('/api-keys')
  return res.data
}

export async function createApiKey(name: string): Promise<import('@/types').ApiKeyCreated> {
  const res = await api.post('/api-keys', { name })
  return res.data
}

export async function revokeApiKey(id: number): Promise<void> {
  await api.delete(`/api-keys/${id}`)
}

// ─── Team invites ─────────────────────────────────────────────────────────────

/**
 * Accept a team invitation. The backend emails links of the form
 * `${APP_URL}/team/accept-invite?token=…` and exposes a JWT-authed
 * POST /teams/accept-invite that takes the token as a QUERY param (no body).
 * The JWT is auto-attached by the axios request interceptor.
 */
export async function acceptTeamInvite(token: string) {
  const { data } = await api.post('/teams/accept-invite', null, {
    params: { token },
  })
  return data
}

// INV-2: pre-signup invite introspection. GET /teams/invite-introspect
// answers "who was this invite token sent to, and by which workspace" so
// /signup can prefill + lock the email field and show the org name. The
// endpoint returns 200 {email, org_name, valid} for a live token and 404
// otherwise. This helper NEVER throws: a 404, a network drop, or an
// environment where the endpoint has not shipped yet all resolve to null,
// and the signup page falls back to its normal blank form. The invite
// journey must degrade, not break, when introspection is unavailable.
export interface TeamInviteIntrospection {
  email: string
  org_name: string
  valid: boolean
}

export async function introspectTeamInvite(
  token: string,
): Promise<TeamInviteIntrospection | null> {
  if (!token) return null
  try {
    const { data } = await api.get('/teams/invite-introspect', {
      params: { token },
    })
    if (
      data &&
      typeof data.email === 'string' &&
      data.email.length > 0 &&
      data.valid !== false
    ) {
      return {
        email: data.email,
        org_name: typeof data.org_name === 'string' ? data.org_name : '',
        valid: true,
      }
    }
    return null
  } catch {
    return null
  }
}

// ─── Account deletion ──────────────────────────────────────────────────────

// The body confirms intent: the user types their own email, and we send it so
// the backend can reject a mistyped or mismatched address before scheduling
// the deletion. Reason is optional and only used for our own product notes.
export interface DeleteAccountBody {
  email: string
  reason?: string
}

export interface DeleteAccountResult {
  status: string
  // ISO timestamp the account is scheduled to be permanently deleted. The
  // account enters a 7-day grace period; deletion can be reversed via
  // cancelAccountDeletion() (or by signing in again) before this date.
  // Matches the backend POST /account/delete response key (`scheduled_for`).
  scheduled_for?: string
  message?: string
}

export interface CancelDeletionResult {
  status: string
  message?: string
}

export interface DeletionStatus {
  pending: boolean
  scheduled_for: string | null
}

/**
 * Request deletion of the signed-in user's own account. The backend
 * verifies the supplied email matches the authenticated user, then schedules
 * the account for deletion after a 7-day grace period (reversible via
 * cancelAccountDeletion or by signing in again before it finalises). This is
 * the first-party account path, distinct from /privacy/erasure-request,
 * which is for third-party data subjects.
 */
export async function deleteAccount(
  body: DeleteAccountBody,
): Promise<DeleteAccountResult> {
  const res = await api.post<DeleteAccountResult>('/account/delete', body)
  return res.data
}

/** Cancel a pending account-deletion request during the grace period. */
export async function cancelAccountDeletion(): Promise<CancelDeletionResult> {
  const res = await api.post<CancelDeletionResult>('/account/cancel-deletion')
  return res.data
}

/** Whether the signed-in account has a pending deletion + when it finalises. */
export async function getDeletionStatus(): Promise<DeletionStatus> {
  const res = await api.get<DeletionStatus>('/account/deletion-status')
  return res.data
}

// ─── Data export (GDPR Article 15 / 20) ─────────────────────────────────────

/**
 * Download a machine-readable copy of the signed-in user's personal data.
 * Backend: POST /account/export returns a JSON attachment (StreamingResponse
 * with a Content-Disposition filename) and writes an `account.data_exported`
 * audit log. This is the self-serve access/portability right advertised on
 * /privacy/data-subject-rights. Returns the raw Blob so the caller can trigger
 * a client-side download without the bytes ever touching a third party.
 */
export async function exportAccountData(): Promise<Blob> {
  const res = await api.post('/account/export', {}, { responseType: 'blob' })
  return res.data as Blob
}

export default api
